import dayjs from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween.js";
import isLeapYearPlugin from "dayjs/plugin/isLeapYear.js";
import isSameOrAfterPlugin from "dayjs/plugin/isSameOrAfter.js";
import isSameOrBeforePlugin from "dayjs/plugin/isSameOrBefore.js";
import utc from "dayjs/plugin/utc.js";
import { formatDateRelative } from "shared/utils/format";
import { assertIsDefined } from "shared/utils/utils";
import { monthDaySchema } from "shared/validation/monthDaySchema";

dayjs.extend(utc);
dayjs.extend(isBetweenPlugin);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(isLeapYearPlugin);

export function getIsValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

export const isDateInThePast = (date: Date) => {
  const now = new Date();
  return now > date;
};

export const getDaysFromToday = (date: Date) => {
  const dayjsInstance = dayjs(date).utc().startOf("day");
  const today = dayjs().utc().startOf("day");
  const diffDays = dayjsInstance.diff(today, "days");
  return diffDays;
};

export function isBusinessDay(date: Date) {
  // 0 - Sunday, 6 - Saturday
  const day = dayjs(date).utc().day();
  return day > 0 && day < 6;
}

function isSunday(date: Date) {
  // 6 - Sunday
  const day = date.getDay();
  return day === 6;
}

export const getBusinessDaysFromToday = (date: Date) => {
  const dayjsInstance = dayjs(date).utc().startOf("day");
  const today = dayjs().utc().startOf("day");
  const diff = dayjsInstance.diff(today, "days");
  const diffAbsolute = Math.abs(diff);
  const pastDate = diff < 0;
  const diffAccountingForWeekendStart =
    !isBusinessDay(today.toDate()) && pastDate ? diff + 1 : diff;
  let numberOfWeekendDaysBetweenStartAndEnd = 0;
  for (let i = 1; i < diffAbsolute + 1; i++) {
    const relevantDay = pastDate ? today.subtract(i, "days") : today.add(i, "days");
    if (!isBusinessDay(relevantDay.toDate())) {
      numberOfWeekendDaysBetweenStartAndEnd++;
    }
  }

  if (isSunday(today.toDate()) && diff === -1) {
    return diffAccountingForWeekendStart;
  }

  // This augments the diff to remove the weekend days from the calculation
  if (pastDate) {
    return diffAccountingForWeekendStart + numberOfWeekendDaysBetweenStartAndEnd;
  } else {
    return diffAccountingForWeekendStart - numberOfWeekendDaysBetweenStartAndEnd;
  }
};

export const getDiffMinutes = (date: Date) => {
  const dayjsInstance = dayjs(date).utc();
  const diff = dayjs().utc().diff(dayjsInstance, "minutes");
  return diff;
};

export const getDiffDaysFromDates = (dateStart: Date, dateEnd: Date) => {
  const dayjsStart = dayjs(dateStart).utc().startOf("day");
  const dayjsEnd = dayjs(dateEnd).utc().startOf("day");
  const diff = dayjsStart.diff(dayjsEnd, "days");
  return diff;
};

export const isToday = (date: Date) => {
  const daysFromToday = getDaysFromToday(date);
  return daysFromToday === 0;
};

export const getIsCoverageActive = (policyEffective: Date | null | undefined) => {
  const isCoverageActive = policyEffective
    ? isToday(policyEffective) || isDateInThePast(policyEffective)
    : null;
  return isCoverageActive;
};

export const getDateStringFromShortString = (monthAndDay: string): string => {
  // Fix year to make sure Feb 29 is a valid date
  return dayjs(`${monthAndDay}/2004`, "M/D/YYYY").format("MMMM DD");
};

/**
 * Given a `Date` object, returns another `Date` object
 * in the next business day.
 *
 * e.g. Wed Jul 27 2022 10:36:27 => Thu Jul 28 2022 10:36:27
 * e.g. Fri Jul 29 2022 10:36:27 => Mon Aug 01 2022 10:36:27
 *
 * @param date
 * @returns
 */
export function getNextBusinessDay(date: Date) {
  const dateDate = date.getDate();
  const dateDay = date.getDay();

  const nextBusinessDay = new Date(date);

  if (dateDay === 5 || dateDay === 6) {
    // Friday and Saturday
    nextBusinessDay.setDate(dateDate + 3);
  } else if (dateDay === 0) {
    // Sunday
    nextBusinessDay.setDate(dateDate + 2);
  } else {
    nextBusinessDay.setDate(dateDate + 1);
  }

  return nextBusinessDay;
}

export function getIsTodayAfterNextBusinessDayOf(date: Date) {
  const nextBusinessDay = getNextBusinessDay(date);
  const now = new Date();
  const isTodayAfterNextBusinessDayOf = now > nextBusinessDay;
  return isTodayAfterNextBusinessDayOf;
}

/**
 * Returns true if the difference between the dates exceeds 30 days.
 * Inputs must be in format MM/DD.
 * iF endDate appears to be before startDate (e.g. start: 12/10, end: 01/25),
 * assume that the endDate is in the following year.
 *
 * @param startDate MM/DD
 * @param endDate MM/DD
 */
export function getExceeds30Days(startDate: string, endDate: string) {
  try {
    monthDaySchema.validateSync(startDate);
    monthDaySchema.validateSync(endDate);
  } catch {
    return null;
  }

  const [startDateMonth, startDateDay] = startDate.split("/");
  const [endDateMonth, endDateDay] = endDate.split("/");

  assertIsDefined(startDateMonth, "startDateMonth");
  assertIsDefined(startDateDay, "startDateDay");
  assertIsDefined(endDateMonth, "endDateMonth");
  assertIsDefined(endDateDay, "endDateDay");

  // any non-leap year
  // following year must also be non-leap
  const referenceYear = 2001;

  const startDateDayjs = dayjs()
    .year(referenceYear)
    .month(Number.parseInt(startDateMonth, 10) - 1)
    .date(Number.parseInt(startDateDay, 10));
  const endDateDayjs = dayjs()
    .year(referenceYear)
    .month(Number.parseInt(endDateMonth, 10) - 1)
    .date(Number.parseInt(endDateDay, 10));

  const diff = endDateDayjs.isSameOrAfter(startDateDayjs)
    ? endDateDayjs.diff(startDateDayjs, "days")
    : endDateDayjs.add(1, "year").diff(startDateDayjs, "days");

  const exceeds30Days = diff > 30;
  return exceeds30Days;
}

type DateRange = {
  startDate: Date;
  endDate: Date;
};

export const getPreviousMonthRange = (inputDate: Date): DateRange => {
  const date = new Date(inputDate.getTime());
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);

  const startDate = new Date(date.getTime());

  date.setMonth(date.getMonth() + 1);
  date.setDate(0);

  const endDate = new Date(date.getTime());

  return {
    startDate,
    endDate,
  };
};

const timezoneAdjustedDayjs = (d: dayjs.Dayjs) => d.subtract(d.utcOffset(), "minute");

export const timezoneAdjustedDate = (date: Date) => timezoneAdjustedDayjs(dayjs(date)).toDate();

export const isBefore = (date: Date) => dayjs().isBefore(timezoneAdjustedDate(date));

export const isAfter = (date: Date) => dayjs().isAfter(timezoneAdjustedDate(date));

export const isSameOrAfter = (date: Date) => dayjs().isSameOrAfter(timezoneAdjustedDate(date));

export const isBetween = (startDate: Date, endDate: Date) =>
  dayjs().isBetween(
    timezoneAdjustedDate(startDate),
    timezoneAdjustedDate(addDays(endDate, 1)),
    undefined,
    "[)",
  );

export const daysUntil = (date: Date) =>
  timezoneAdjustedDayjs(dayjs(date)).diff(dayjs().startOf("day"), "days");

export const addDays = (date: Date, days: number) => dayjs(date).add(days, "day").toDate();

export const buildDate = (policyEffective: Date) => {
  const dayjsInstance = dayjs(policyEffective).utc();
  const month = dayjsInstance.format("MMMM");
  const day = dayjsInstance.date();
  const daysDiffString = formatDateRelative(policyEffective);
  return { month, day, daysDiffString };
};

export const isPeakSeason = () => {
  const now = dayjs();
  const peakSeasonStart = dayjs()
    .month(10)
    .date(20)
    .year(now.month() <= 1 ? now.year() - 1 : now.year());
  const peakSeasonEnd = dayjs()
    .month(0)
    .date(31)
    .year(now.month() >= 10 ? now.year() + 1 : now.year());

  return (
    now.startOf("day").isSameOrAfter(peakSeasonStart.startOf("day")) &&
    now.endOf("day").isSameOrBefore(peakSeasonEnd.endOf("day"))
  );
};

export const isLeapYear = () => {
  return dayjs().isLeapYear();
};
